import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Typography,
  IconButton,
  Checkbox,
  Box,
  Tooltip,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomizeIcon from "src/@core/components/icons/CustomizeIcon";

/**
 * BusinessRefundSelectionDialog Component
 * Dialog for business to select specific items to refund
 * Similar to BusinessCancelSelectionDialog but for refunds
 */
const BusinessRefundSelectionDialog = ({
  open,
  onClose,
  dialogData,
  tempSelection,
  onCheckboxChange,
  onSubmit,
}) => {
  // Get the variant or item data
  const getItemData = (itemId) => {
    if (dialogData?.isVariant && dialogData?.variant) {
      return dialogData.variant.items?.find(vi => vi._id === itemId);
    } else if (!dialogData?.isVariant && dialogData?.item) {
      return dialogData.item.items?.find(si => si._id === itemId);
    }
    return null;
  };

  // Check if item has customizations
  const hasCustomizations = (itemData) => {
    return itemData?.customizations?.ingredientModifications?.some(
      mod => mod.action !== "no_modification"
    );
  };

  // Check if item has sides
  const hasSides = (itemData) => {
    return itemData?.customizations?.sides && itemData.customizations.sides.length > 0;
  };

  // Get customizations text
  const getCustomizationsText = (itemData) => {
    if (!hasCustomizations(itemData)) return null;
    
    return itemData.customizations.ingredientModifications
      .filter(mod => mod.action !== "no_modification")
      .map((mod, index) => {
        let text = "";
        if (mod.action === "substitute") {
          text = `Substitute ${mod.ingredientName} with ${mod.substituteWith}`;
        } else if (mod.action === "remove") {
          text = `Remove ${mod.ingredientName}`;
        } else if (mod.action === "add") {
          text = `Add extra ${mod.ingredientName}`;
        }
        return text;
      })
      .filter(Boolean);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        zIndex: 10002, 
      }}
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
        }
      }}
    >
      {/* Dialog Header */}
      <DialogTitle sx={{ 
        borderBottom: "1px solid #E0E0E0", 
        pb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between"
      }}>
        <Typography variant="h6" sx={{ textTransform: "lowercase" }}>
          {dialogData?.isVariant 
            ? `${dialogData?.itemName} - ${dialogData?.variantName} variant`
            : dialogData?.itemName
          }
        </Typography>
        <IconButton 
          size="small" 
          onClick={onClose}
          sx={{ ml: 2 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      {/* Dialog Content */}
      <DialogContent sx={{ pt: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, textTransform: "lowercase" }}>
          select items to refund:
        </Typography>
        
        <List sx={{ width: "100%" }}>
          {/* Individual Items */}
          {dialogData?.allItemIds.map((itemId) => {
            const itemKey = dialogData.isVariant 
              ? `${dialogData.menuItemId}-${dialogData.variantName}-${itemId}`
              : `${dialogData.menuItemId}-${itemId}`;
            
            // Check if this item is already refunded
            const itemData = getItemData(itemId);
            const isRefunded = itemData?.refundRequest === true;
            const isChecked = isRefunded || tempSelection.has(itemKey);
            const isDisabled = isRefunded;
            
            const customizations = getCustomizationsText(itemData);
            const itemHasSides = hasSides(itemData);
            
            return (
              <ListItem 
                key={itemKey}
                sx={{
                  border: "1px solid #E0E0E0",
                  borderRadius: 1,
                  mb: 1,
                  "&:hover": { bgcolor: isDisabled ? "transparent" : "action.hover" },
                  cursor: isDisabled ? "not-allowed" : "pointer",
                  opacity: isDisabled ? 0.5 : 1,
                  flexDirection: "column",
                  alignItems: "flex-start"
                }}
                onClick={() => {
                  if (isDisabled) return;
                  
                  const newSelection = new Set(tempSelection);
                  if (newSelection.has(itemKey)) {
                    newSelection.delete(itemKey);
                  } else {
                    newSelection.add(itemKey);
                  }
                  onCheckboxChange(newSelection);
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
                  <Checkbox
                    checked={isChecked}
                    disabled={isDisabled}
                    onChange={() => {
                      if (isDisabled) return;
                      
                      const newSelection = new Set(tempSelection);
                      if (newSelection.has(itemKey)) {
                        newSelection.delete(itemKey);
                      } else {
                        newSelection.add(itemKey);
                      }
                      onCheckboxChange(newSelection);
                    }}
                    sx={{ mr: 2 }}
                  />
                  <ListItemText
                    primary={
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="body2" sx={{ textTransform: "lowercase" }}>
                          item {itemData?.itemNumber || itemId}
                          {isRefunded && " (already refunded)"}
                        </Typography>
                        {hasCustomizations(itemData) && (
                          <Tooltip
                            title={
                              <Box sx={{ p: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, textTransform: "lowercase" }}>
                                  customizations:
                                </Typography>
                                {customizations.map((text, index) => (
                                  <Typography key={index} variant="caption" sx={{ display: "block", textTransform: "lowercase", mb: 0.5 }}>
                                    • {text}
                                  </Typography>
                                ))}
                              </Box>
                            }
                            arrow
                            placement="top"
                            PopperProps={{ style: { zIndex: 10001 } }}
                          >
                            <IconButton size="small" sx={{ p: 0.5 }}>
                              <CustomizeIcon size="16" color="#6200EE" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                </Box>
                
                {/* Show sides if present */}
                {itemHasSides && (
                  <Box sx={{ pl: 7, mt: 0.5, width: "100%" }}>
                    {itemData.customizations.sides.map((side, sideIndex) => (
                      <Typography key={sideIndex} variant="caption" sx={{ display: "block", textTransform: "lowercase", mb: 0.5 }}>
                        {side.sideName} + ${side.price}{side.quantity > 1 ? ` (qty: ${side.quantity})` : ''}
                      </Typography>
                    ))}
                  </Box>
                )}
              </ListItem>
            );
          })}
        </List>
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions sx={{ borderTop: "1px solid #E0E0E0", p: 2, gap: 1 }}>
        <Button 
          onClick={onSubmit}
          variant="contained"
          sx={{ textTransform: "lowercase", mt: 1 }}
        >
          add to refund
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BusinessRefundSelectionDialog;

